/**
 * @fileoverview Connector Management Page Component
 * 
 * Manages third-party connectors for document processing integrations.
 * Supports multiple connector types (SalesForce, AdobeSign, SignNow, Docusign).
 * Provides CRUD operations for connectors with authentication credentials.
 * Integrates with jQuery DataTables for advanced table rendering.
 * 
 * @module components/pages/connector
 * @requires React - UI framework with hooks
 * @requires react-bootstrap - Bootstrap components
 * @requires react-select - Dropdown component
 * @requires jquery - DOM manipulation and DataTables
 * @requires admin - API service for connector CRUD
 * 
 * @component
 * @example
 * { path: '/connectors', name: 'Connectors', element: <Connectors />, isPrivate: true }
 * 
 * Features:
 * - View all connectors in paginated DataTable (25 rows/page)
 * - Create new connector with name, type, org ID, API token, secret key
 * - Edit existing connector configuration
 * - Delete connectors with confirmation
 * - Real-time search across connector properties
 * - Support for multiple connector types (SalesForce, Docusign, etc.)
 * - Process loader during API operations
 * - Success/error messages via toast
 */

import React, { useEffect, useState } from "react";
import { Tooltip, OverlayTrigger, Card } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { default as Select, components }  from "react-select";
import $ from "jquery";
import * as ReactDOM from 'react-dom';
import "datatables.net";
import {getFilenameFromURL,getUsername,formatDate} from "../../utils/common_functions"
import {API_SUCCESS_RESPONSE_CODE,API_SUCCESS_RESPONSE_CREATED_CODE} from "../../utils/config_mapping"
import CommonIcon from '../common/customicon';
import PageLoader from '../common/pageloader';
import ProcessLoader from '../common/processloader';
import "../../../node_modules/datatables.net-dt/css/dataTables.dataTables.css";
import {getAllTaxonomy,createTaxonomy,deleteTaxonomy,updateTaxonomy, getAllClauses} from "../../services/admin"
import Message from "../common/message";
// import ReactJson from 'react-json-view';
import HeaderLogo  from "../structure/headerlogo";

/**
 * Available Connector Types
 * 
 * Dropdown options for selecting connector type to configure.
 * Each type represents a different third-party service.
 * 
 * @type {Array<{value: string, label: string}>}
 */
const taxonomies_options = [
  { value: 'SalesForce', label: 'SalesForce' },
  { value: 'Adobesign', label: 'Adobesign' },
  { value: 'SignNow', label: 'SignNow' },
  { value: 'Docusign', label: 'Docusign' },
];

/**
 * Mock Connector Data
 * 
 * Sample data structure for demonstrating connector configuration.
 * Contains example SalesForce and Docusign connectors with JWT tokens.
 * 
 * @type {Object}
 * @property {number} code - API response code (200 = success)
 * @property {Array} data - Array of connector objects with auth credentials
 */
const jsondata  = {
  "success": true,
  "data": [
      {
          "_id": "67773ffd923d439086ccaa23",
          "connectorName": "SalesForce Connector",
          "connectorType": "SalesForce",
          "connectorOrganizationID": "Predactica",
          "connectorAPIToken":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyaWQiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c",
    "connectorSecretKey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyaWQiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c",
          "createdBy": "Admin",
          "createdAt": "2025-01-03 01:40:13"
      },
  {
          "_id": "67773ffd923d439086ccaa24",
          "connectorName": "Docusign Connector",
          "connectorType": "Docusign",
          "connectorOrganizationID": "Predactica",
          "connectorAPIToken":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyaWQiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c",
    "connectorSecretKey":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyaWQiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c",
          "createdBy": "Admin",
          "createdAt": "2025-01-03 01:40:13"
      }
    

  ],
  "error": null,
  "code": 200
}

/**
 * Connectors Component
 * 
 * Main component for managing third-party connectors.
 * Handles modal states, data management, and DataTable initialization.
 * 
 * @returns {React.ReactElement} Connector management UI
 */
export const Connectors = () => {
  /**
   * Custom Select Option Component
   * 
   * Renders checkbox-style select options for multi-select dropdown.
   * Used for selecting clauses to associate with connectors.
   * 
   * @param {Object} props - React Select component props
   * @param {boolean} props.isSelected - Whether option is currently selected
   * @param {string} props.label - Display label for option
   * @returns {React.ReactElement} Checkbox option element
   */
  const Option = (props) => {
    return (
      <div>
        <components.Option {...props}>
          <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
          />{" "}
          <label>{props.label}</label>
        </components.Option>
      </div>
    );
  };

  // ============================================================================
  // MODAL STATE MANAGEMENT
  // ============================================================================
  
  /**
   * Add/Edit Connector Modal
   * 
   * Controls visibility of connector creation/editing modal.
   * Displays form for entering connector configuration details.
   * @type {[boolean, Function]}
   */
  const [addclausemodal, setAddClausesModal] = useState(false);
  
  /**
   * Clause Data State
   * 
   * Stores list of available clauses for multi-select dropdown.
   * Used to associate clauses with connectors.
   * @type {[Array, Function]}
   */
  const [clausesData, setClausesData] = useState([]); // Clause options
  const handleClose = () => setAddClausesModal(false);
  const handleShow = () => setAddClausesModal(true);

  /**
   * Prompt View Modal
   * 
   * Displays connector's prompt information in JSON format.
   * Triggered by "View Prompt" button in table.
   * @type {[boolean, Function]}
   */
  const [promptmodal, setAddPromptModal] = useState(false);
  const handlePromptClose = () => setAddPromptModal(false);
  const handlePromptShow = () => setAddPromptModal(true);

  /**
   * Delete Confirmation Modal
   * 
   * Confirms connector deletion with ID verification.
   * Displayed when user clicks delete action.
   * @type {[boolean, Function]}
   */
  const [deletedocumentmodal, setDeleteClausesModal] = useState(false);
  const handleDeleteClose = () => setDeleteClausesModal(false);
  const handleDeleteShow = () => setDeleteClausesModal(true);
  
  // ============================================================================
  // DATA STATE MANAGEMENT
  // ============================================================================
  
  /**
   * Loading State
   * 
   * Indicates whether connector data is being fetched from API.
   * Used to display loading spinner while data loads.
   * @type {[boolean, Function]}
   */
  const [loading, setLoading] = useState(true); // State to track loading
  
  /**
   * Connectors Data State
   * 
   * Stores array of all connector objects retrieved from API.
   * Each connector includes: _id, connectorName, connectorType, connectorOrganizationID,
   * connectorAPIToken, connectorSecretKey, createdBy, createdAt
   * @type {[Array<Object>, Function]}
   */
  const [data, setData] = useState([]); // State to hold API data

  /**
   * Prompt Data State
   * 
   * Stores prompt information for currently viewed connector.
   * Displayed in prompt view modal.
   * @type {[Array, Function]}
   */
  const [prompt_data, setPromptData] = useState([]); // State to hold API data

  /**
   * Filtered Data State
   * 
   * Holds filtered connector data based on search query and filters.
   * Used by DataTable for display after applying search/filter logic.
   * @type {[Array<Object>, Function]}
   */
  const [filteredData, setFilteredData] = useState([]);
  
  /**
   * Search Query State
   * 
   * Stores current search term for real-time filtering.
   * Case-insensitive search across connector name, type, org ID.
   * @type {[string, Function]}
   */
  const [searchQuery, setSearchQuery] = useState("");
  
  /**
   * Clause Filter Text State
   * 
   * Stores search term for filtering available clauses in dropdown.
   * Used in clause selection modal.
   * @type {[string, Function]}
   */
  const [filterTextClauses, setFilterTextClauses] = useState(""); // For clause filtering
  
  /**
   * Selected Clauses State
   * 
   * Array of clause IDs associated with current connector.
   * Used for multi-select dropdown in add/edit modal.
   * @type {[Array<string>, Function]}
   */
  const [selectedClauses, setSelectedClauses] = useState([]); // Selected clauses
  
  /**
   * Global Filters State
   * 
   * Stores active filters for connector list display.
   * Properties:
   * - filterstatus: Status filter (null = all)
   * - filterTextSearch: General search text filter
   * @type {[Object, Function]}
   */
  const [filters, setFilters] = useState({
  filterstatus: null,
  filterTextSearch: ""
});

// ============================================================================
// FORM STATE MANAGEMENT
// ============================================================================

const initialState = {
    connectorName:"",
    taxonomyId:"",
    connectorName:"",
    connectorType:"",
    connectorOrganizationID:"",
    connectorAPIToken:"",
    connectorSecretKey:""
}

/**
 * Connector Form Data State
 * 
 * Manages form input values for creating/editing connectors.
 * Reset to initialState on modal close or after successful save.
 * Properties:
 * - connectorName: Name of the connector
 * - taxonomyId: Database ID (empty for new, populated for edit)
 * - connectorType: Selected type (SalesForce, Docusign, etc.)
 * - connectorOrganizationID: Organization identifier
 * - connectorAPIToken: API authentication token
 * - connectorSecretKey: Secret key for authentication
 * @type {[Object, Function]}
 */

  const [formData, setFormData] = useState(initialState);
  
  /**
   * Form Validation Errors State
   * 
   * Stores field-level validation errors.
   * Keys correspond to form field names.
   * Cleared when form is reset.
   * @type {[Object, Function]}
   */
  const [errors, setErrors] = useState({});

  // ============================================================================
  // API HANDLER FUNCTIONS
  // ============================================================================

  /**
   * Handle Connector Deletion
   * 
   * Deletes connector from database via deleteTaxonomy API.
   * Triggered by delete button click in table row.
   * 
   * Process:
   * 1. Extract connector ID from data-id attribute
   * 2. Call deleteTaxonomy(dataId) API endpoint
   * 3. Verify API response code (200 = success)
   * 4. Close delete modal
   * 5. Refresh connector list via fetchData()
   * 6. Display success message via toast
   * 7. Catch and log any errors
   * 
   * @async
   * @param {Event} event - Click event from delete button
   * @returns {Promise<void>}
   */
   const handleDelete = async (event) => {
      const dataId = event.target.getAttribute('data-id');
      const targetElement = document.getElementById("app_err");
      if(dataId){
        try {
            const result = await deleteTaxonomy(dataId);
            if(result!==undefined){
              if (result.code === API_SUCCESS_RESPONSE_CODE) {
                handleDeleteClose()
                fetchData();
                if (targetElement) {
                  ReactDOM.unmountComponentAtNode(targetElement);
                }
                ReactDOM.render(<Message type='success' message={result.data.message} />, targetElement);
                
              }
            }
        } catch (error) {
          console.error("Error refreshing token:", error);
        }
      }
    };

  /**
   * Filter Clauses by Search Text
   * 
   * Filters available clauses based on user's search input.
   * Performs case-insensitive comparison on clauseName.
   * Used in clause selection dropdown.
   * 
   * @type {Array<Object>}
   */
  const filteredClauses = clausesData.filter((clause) =>
    clause.clauseName.toLowerCase().includes(filterTextClauses.toLowerCase())
  );

  /**
   * Handle Individual Clause Selection
   * 
   * Toggles clause selection in multi-select dropdown.
   * Adds clause if not selected, removes if already selected.
   * Maintains selectedClauses array for form submission.
   * 
   * @param {Object} clause - Clause object to toggle
   * @param {string} clause.clauseName - Clause identifier
   * @returns {void}
   */
  const handleClauseSelection = (clause) => {
  setSelectedClauses(prevSelected => 
    prevSelected.includes(clause)
      ? prevSelected.filter(c => c !== clause) // Remove if already selected
      : [...prevSelected, clause] // Add to selected if not already there
  );
};

  /**
   * Handle Connector Save (Create/Update)
   * 
   * Saves connector to database via createTaxonomy or updateTaxonomy API.
   * Differentiates between create (empty taxonomyId) and update operations.
   * 
   * Create Flow:
   * 1. taxonomyId is empty string ("")
   * 2. Call POST createTaxonomy(pmData) API
   * 3. Expect 201 CREATED response code
   * 
   * Update Flow:
   * 1. taxonomyId is populated with existing connector ID
   * 2. Call PUT updateTaxonomy(pmData) API
   * 3. Expect 200 OK response code
   * 
   * Success Actions:
   * - Close add/edit modal
   * - Refresh connector list via fetchData()
   * - Display success toast message
   * - Clear form errors
   * 
   * Error Handling:
   * - Log error details
   * - Show error message in toast
   * 
   * @async
   * @param {Object} pmData - Connector form data
   * @param {string} pmData.connectorName - Connector name
   * @param {string} pmData.taxonomyId - Database ID (empty for create)
   * @param {string} pmData.connectorType - Connector type
   * @param {string} pmData.connectorOrganizationID - Organization ID
   * @param {string} pmData.connectorAPIToken - API token
   * @param {string} pmData.connectorSecretKey - Secret key
   * @returns {Promise<void>}
   */
    const handleSaveConnector = async (pmData) => {
        const targetElement = document.getElementById("app_err");
        // Check if creating new connector (empty taxonomyId)
        if(pmData.taxonomyId===""){
          const result = await createTaxonomy(pmData);
          // 201 CREATED = successful creation
          if (result.code === API_SUCCESS_RESPONSE_CREATED_CODE) {
            fetchData();
            handleClose()
            if (targetElement) {
              ReactDOM.unmountComponentAtNode(targetElement);
            }
            ReactDOM.render(<Message type='success' message={result.data.message} />, targetElement);
           
          } else {
            console.warn("Unexpected response status:", result.status);
          }
        }else{
          // Update existing connector
          const result = await updateTaxonomy(pmData.taxonomyId,pmData);
          // 200 OK = successful update
          if (result.code === API_SUCCESS_RESPONSE_CODE) {
            fetchData();
            handleClose()
            if (targetElement) {
              ReactDOM.unmountComponentAtNode(targetElement);
            }
            ReactDOM.render(<Message type='success' message={result.data.message} />, targetElement);
          } else {
            console.warn("Unexpected response status:", result.status);
          }
        }
    };

    /**
     * Fetch Connectors from API
     * 
     * Retrieves all connectors from database via getAllTaxonomy API.
     * 
     * NOTE: Currently returns mock data (jsondata.data) from line 431.
     * Early return statement (line 432) prevents API call execution.
     * Production version would execute getAllTaxonomy() API below.
     * 
     * API Flow (when enabled):
     * 1. Show loading spinner
     * 2. Call GET /taxonomy endpoint (getAllTaxonomy)
     * 3. Verify 200 response code
     * 4. Update data state with API result
     * 5. Update filteredData with same data
     * 6. Hide loading spinner
     * 7. Catch and log errors
     * 
     * @async
     * @returns {Promise<void>}
     */
    const fetchData = async () => {
      // Returns mock data immediately - bypasses API call
      setData(jsondata.data);
      setFilteredData(jsondata.data); // Set initial data for filtering
      setLoading(false); // Stop loading spinner
      return;
      
      // ===== Unreachable Code Below (bypassed by return statement) =====
      $(".page_pro_load").hide();
      setLoading(true); // Stop loading spinner
      try {
        const result = await getAllTaxonomy();
        if (result.code === API_SUCCESS_RESPONSE_CODE) {
          setData(result.data);
          setFilteredData(result.data); // Set initial data for filtering
        } else {
          console.warn("Unexpected response status:", result.status);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      finally {
         setLoading(false); // Stop loading spinner
     }
    };
    
    /**
     * useEffect Hook - Initial Data Load
     * 
     * Runs on component mount (empty dependency array []).
     * Fetches connector list from API or mock data.
     * 
     * Executes:
     * 1. fetchData() to load connectors
     */
 useEffect(() => {
      fetchData();
 }, []); // Empty dependency array ensures this runs only once

/**
 * Select All Clauses State
 * 
 * Tracks whether "Select All" checkbox is enabled.
 * Used to bulk select/deselect all available clauses.
 * @type {[boolean, Function]}
 */
const [selectAll, setSelectAll] = useState(false);

/**
 * Handle Select All Functionality
 * 
 * Toggles between selecting all filtered clauses and clearing selection.
 * 
 * When unchecked (selectAll is true):
 * - Clear selectedClauses array
 * - Set selectAll to false
 * 
 * When checked (selectAll is false):
 * - Select all clauses in filteredClauses list
 * - Set selectAll to true
 * 
 * @returns {void}
 */
const handleSelectAll = () => {
  if (selectAll) {
    // If "Select All" is already checked, unselect all clauses
    setSelectedClauses([]);
    setSelectAll(false);
  } else {
    // Select all currently filtered clauses
    const allFilteredClauses = filteredClauses.map(clause => clause.clauseName);
    setSelectedClauses(allFilteredClauses);
    setSelectAll(true);
  }
};

/**
 * Handle Individual Clause Click
 * 
 * Toggles selection state for a single clause.
 * Updates selectedClauses array.
 * 
 * @param {string} clause - Clause name/ID to toggle
 * @returns {void}
 */
  const handleClauseClick = (clause) => {
    setSelectedClauses(prevSelected =>
      prevSelected.includes(clause)
        ? prevSelected.filter(c => c !== clause)
        : [...prevSelected, clause]
    );
  };

  /**
   * Fetch Clauses from API
   * 
   * Retrieves all available clauses for association with connectors.
   * Called on component mount.
   * 
   * API Flow:
   * 1. Call GET /clauses endpoint (getAllClauses)
   * 2. Verify 200 response code
   * 3. Update clausesData state with result
   * 4. Catch and log any errors
   * 
   * @async
   * @returns {Promise<void>}
   */
 const fetchClauseData = async () => {
  try {
    const result = await getAllClauses();
    if (result.code === API_SUCCESS_RESPONSE_CODE) {
      setClausesData(result.data);
    } else {
      console.warn("Unexpected response status:", result.status);
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  }
 }

 /**
  * useEffect Hook - Load Clauses
  * 
  * Runs on component mount (empty dependency array []).
  * Fetches available clauses from API.
  * 
  * Executes:
  * 1. fetchClauseData() to load clause list
  */
 useEffect(() => {
  fetchClauseData();
}, []); // Empty dependency array ensures this runs only once

  /**
   * useEffect Hook - Initialize DataTable
   * 
   * Initializes jQuery DataTable when filteredData changes.
   * Destroys existing table before reinitializing to prevent duplicates.
   * 
   * DataTable Features:
   * - Pagination: 25 rows per page (pageLength: 25)
   * - Sorting: By first column (columnDefs order)
   * - Scrolling: Horizontal scroll enabled (scrollX: true)
   * - Responsive: Collapse enabled (scrollCollapse: true)
   * - dom: '<"top"f>rt<"bottom"ilp><"clear">' - Layout: search, table, info, length, pagination
   * 
   * Column Configuration:
   * - Index 0-3: text-left alignment (connectorName, connectorType, etc.)
   * - Index 4-5: text-center alignment (createdBy, Actions)
   * 
   * Event Handlers:
   * - .showprompt-btn: Click handler shows prompt modal
   * - .view-btn: Click handler shows edit modal with connector data
   * - .delete-btn: Click handler shows delete confirmation modal
   * 
   * Executes on Data Changes:
   * - filteredData updates trigger table recreation
   * - searchQuery updates trigger table recreation
   */
  useEffect(() => {
    if (filteredData.length > -1) {
      const table = $("#contract_grid").DataTable({
        // Custom row class for empty rows
        createdRow: function(row, data, dataIndex) {
          if (data.length === 1) {
              $(row).addClass('empty-row');
          }
          },
        // Table Layout
        scrollCollapse: true,
        scrollX: true,
        pageLength: 25, // Set default number of records per page
       // scrollY:$(window).height()-($("#table_height").height()+180),
        data: filteredData,
        destroy: true, // Ensure the previous table is destroyed
        order: [],  // This disables default sorting on any column
        columnDefs: [
          { 
            targets: [3], // Center-align "Created By" column
            className: 'text-left' // Bootstrap's text-center class
          },
          { 
            targets: [4], // Center-align "Created By" column
            className: 'text-center' // Bootstrap's text-center class
          },
          { 
            targets: '_all', // Left-align all other columns
            className: 'text-left' // Bootstrap's text-left class
          }
        ],
        columns: [
          { data: "connectorName", title: "Connector Name",width: "200px", },
          { data: "connectorType", title: "Type",width: "100px", },
          
            { data: "connectorOrganizationID", title: "Organization ID",width: "150px", },
            { data: "connectorAPIToken", title: "API Token",width: "150px", },
            {
              data: "createdAt", // Field name from your data
              title: "Created At",
             // width: "150px",
              render: function (data, type, row) {
                return data;
              }},
              { data: "createdBy", title: "Created By",width: "150px", },
          {
            title: "Actions",
            orderable: false, // Disable sorting for this column
            data: null,
            width: "60px", 
            createdCell: function (td, cellData, rowData, row, col) {
              // Clear the default HTML and replace it with React-rendered content
              ReactDOM.render(
                <>
                  <OverlayTrigger
                    placement="top"
                    trigger={['hover', 'click']}
                    overlay={<Tooltip id="view-top">Edit</Tooltip>}
                  >
                    <a href="javascript:void(0);" className="view-btn" data-rowdata={JSON.stringify(rowData)}>
                      <i className="datatable-pencil-icon"></i>
                    </a>
                  </OverlayTrigger>
                  &nbsp;
                  <OverlayTrigger
                    trigger={['hover', 'click']}
                    placement="top"
                    overlay={<Tooltip id="delte-top">Delete</Tooltip>}
                  >
                  <a href="javascript:void(0);" className="delete-btn" data-bs-toggle="modal"
                     data-bs-target="#confirmationModal" data-id={rowData._id} data-name={rowData.connectorName}>
                    <i className="datatable-delete-icon"></i>
                  </a></OverlayTrigger>
                  &nbsp;
                </>,
                td // Render the content directly into the table cell
              );
            }
          }
                 ],
        fixedColumns: {
          start: 1,
          end: 1
      },
        paging: true,
        searching: false,
        ordering: true,
        responsive: true,
        fixedHeader: true,
        language: {
          emptyTable: "No data found matching the filters",  // Custom message for no data
        },
      });

      /**
       * DataTable Button Event Handler - View Prompt
       * 
       * Click handler for "View Prompt" button (.showprompt-btn).
       * Retrieves rowData from data attribute and displays it in modal.
       * 
       * @listens click
       */
      $("#contract_grid").on("click", ".showprompt-btn", function () {
        const rowData = $(this).data("rowdata");
        setAddPromptModal(true); // Open the modal
        console.log(rowData.prompt)
        setPromptData(rowData.prompt)
      });

      /**
       * DataTable Button Event Handler - Edit Connector
       * 
       * Click handler for edit button (.view-btn) in Actions column.
       * Populates form with selected connector's data for editing.
       * 
       * Steps:
       * 1. Extract rowData from data-rowdata attribute
       * 2. Populate formData with connector values
       * 3. Set connectorType using taxonomies_options mapping
       * 4. Prefill selectedClauses from rowData
       * 5. Open add/edit modal
       * 
       * @listens click
       */
      $("#contract_grid").on("click", ".view-btn", function () {
        const rowData = $(this).data("rowdata");
        setFormData({
          connectorName: rowData.connectorName,
          connectorType: taxonomies_options.find(option => option.value === rowData.connectorType),
          taxonomyId: rowData._id,
          clauses: rowData.clauses || [], // If clauses exist
        });
        setSelectedClauses(rowData.clauses.map(clause => clause.clauseName)); // Prefill the selected clauses
        handleShow(); // Open the modal
      });

      /**
       * DataTable Button Event Handler - Delete Connector
       * 
       * Click handler for delete button (.delete-btn) in Actions column.
       * Displays delete confirmation modal with connector name.
       * 
       * Steps:
       * 1. Extract connector ID from data-id attribute
       * 2. Extract connector name from data-name attribute
       * 3. Set delete modal button data-id attribute
       * 4. Display connector name in modal
       * 5. Open delete confirmation modal
       * 
       * @listens click
       */
      $("#contract_grid").on("click", ".delete-btn", function () {
        handleDeleteShow()
        const id = $(this).data("id");
        const name = getFilenameFromURL($(this).data("name"));
        setTimeout(function() {
          $("#btn_delete").attr("data-id", id);
          $("#document_name").html(name);
        }, 200);
       });

      // Cleanup: Destroy DataTable instance when component unmounts or data changes
      return () => {
        table.destroy(); // Clean up DataTable instance
      };
    }
  }, [filteredData]);

  /**
   * Handle Search Query Change
   * 
   * Updates searchQuery state and applies filters on every keystroke.
   * Triggers real-time filtering of connectors.
   * 
   * @param {Event} e - Input change event
   * @returns {void}
   */
    const handleSearch = (e) => {
      setSearchQuery(e.target.value);
      applyFilters(e.target.value, filters);
    };

  /**
   * Validate Connector Form
   * 
   * Validates all required form fields:
   * - connectorName (required)
   * - connectorType (required)
   * - connectorOrganizationID (required)
   * - connectorAPIToken (required)
   * - connectorSecretKey (required)
   * 
   * Sets errors state for invalid fields.
   * Returns true only if all fields are valid.
   * 
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validateForm = () => {
    const newErrors = {};
    if (!formData.connectorName) newErrors.connectorName = "Connector name  is required.";
    if (!formData.connectorType) newErrors.connectorType = "Connector type  is required.";
    if (!formData.connectorOrganizationID) newErrors.connectorOrganizationID = "Connector type  is required.";
    if (!formData.connectorAPIToken) newErrors.connectorAPIToken = "Connector type  is required.";
    if (!formData.connectorSecretKey) newErrors.connectorSecretKey = "Connector type  is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Handle Form Input Change
   * 
   * Updates formData state for text input fields (name, org ID, token, key).
   * Clears field error when user starts typing.
   * 
   * @param {Event} e - Input change event
   * @param {string} e.target.name - Field name
   * @param {string} e.target.value - New field value
   * @returns {void}
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  /**
   * Handle Connector Type Select Change
   * 
   * Updates connectorType in formData when dropdown selection changes.
   * Stores full option object (value and label).
   * Clears type error when user selects a type.
   * 
   * @param {Object} selectedOption - Selected option from react-select
   * @param {string} selectedOption.value - Option value
   * @param {string} selectedOption.label - Option label
   * @returns {void}
   */
  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      connectorType: selectedOption  // Store the entire selected options array for multi-select
    }));
    setErrors((prevErrors) => ({ ...prevErrors, connectorType: "" })); // Clear error if any
  };
  
  /**
   * Reset Form to Initial State
   * 
   * Clears all form data and resets to empty state.
   * Clears selected clauses.
   * Opens add/edit modal for new connector creation.
   * 
   * @param {Event} e - Click event
   * @returns {void}
   */
  const resetForm = (e) => {
    handleShow()
    setFormData(initialState); // Reset form fields to initial state
    setSelectedClauses([]); // Clear selected clauses
  }

  /**
   * Handle Form Submission
   * 
   * Processes connector form submission for creating/updating connectors.
   * 
   * Steps:
   * 1. Get logged-in username for createdBy field
   * 2. Prevent default form submission
   * 3. Validate all form fields using validateForm()
   * 4. Return early if validation fails
   * 5. Show loading spinner
   * 6. Transform selectedClauses to clause objects array
   * 7. Convert connectorType from option object to string value
   * 8. Call handleSaveConnector with formData
   * 
   * @param {Event} e - Form submission event
   * @returns {void}
   */
  const handleSubmit = (e) => {
    const logged_user_name = getUsername();
    e.preventDefault();
    if (!validateForm()) return;
    $(".page_pro_load").show();
    if(selectedClauses.length > 0){
      formData.clauses = selectedClauses.map(item => ({ clauseName: item }));
      formData.connectorType = formData.connectorType.value
    }
  };

  /**
   * Handle Filter Change
   * 
   * Updates filter state when user changes filter dropdown values.
   * Immediately applies filters to connector list.
   * 
   * @param {string} filterName - Name of filter property
   * @param {*} value - New filter value
   * @returns {void}
   */
  const handleFilterChange = (filterName, value) => {
    setFilters((prevState) => {
      const newFilters = { ...prevState, [filterName]: value };
      applyFilters(newFilters);
      return newFilters;
    });
  };

  /**
   * Apply Filters to Connector List
   * 
   * Filters connectors based on search text and active filters.
   * Performs case-insensitive search across:
   * - connectorName
   * - connectorType
   * - createdBy
   * - createdAt
   * - _id
   * 
   * Uses OR logic: connector matches if any field contains search text.
   * 
   * @param {Object} newFilters - Filter object with filterTextSearch property
   * @param {string} newFilters.filterTextSearch - Text to search for
   * @returns {void}
   */
  const applyFilters = (newFilters) => {
    let filteredDataTemp = data;
    if (newFilters.filterTextSearch) { // New filter for text search
      filteredDataTemp = filteredDataTemp.filter((doc) => 
        doc.connectorName.toLowerCase().includes(newFilters.filterTextSearch.toLowerCase()) ||
        doc.connectorType.toLowerCase().includes(newFilters.filterTextSearch.toLowerCase()) ||
        doc.createdBy.toLowerCase().includes(newFilters.filterTextSearch.toLowerCase()) ||
        doc.createdAt.toLowerCase().includes(newFilters.filterTextSearch.toLowerCase())  ||
        doc._id.toLowerCase().includes(newFilters.filterTextSearch.toLowerCase())
      );
    }
    setFilteredData(filteredDataTemp);
  };

  /**
   * Reset All Filters
   * 
   * Clears search query and all active filters.
   * Resets filteredData to show all connectors.
   * 
   * @returns {void}
   */
  const resetFilters = () => {
    setSearchQuery('');
    setFilters({
      filterdocumentType: null,
      filterlicensor: null,
      filterexpiryDate: '',
      filterstatus: null
    });
    setFilteredData(data); // Reset to full data
  };

  // ============================================================================
  // COMPONENT RENDER
  // ============================================================================

  return (
    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          <div id="table_height">
          <div className="flex-grow-1" id="row_heading">
            <div className="page-title">Connectors</div>
            <p className="text-muted my-2 ff-secondary">
            Manage and organize standard Connectors
            </p>
            <div id="app_err"></div>
          </div>
          
          {/* Search and Action Bar */}
          { data.length > 0 ? 
          <div className="row g-2 mb-4 mt-4" id="row_search">
            {/* Search Box Column */}
            <div className="col-sm-9">
              <div className="search-box">
                <input
                  type="text"
                  className="form-control"
                  id="searchClausess"
                  placeholder="Search Connectors..."
                  onChange={(e) => handleFilterChange('filterTextSearch', e.target.value)}
                />
                <i className="grid-search">
                <CommonIcon iconName="Search" size={"14px"}  />
                </i>
              </div>
            </div>
            
            {/* Action Buttons Column */}
            <div className="col-sm-auto ms-auto">
              <div className="list-grid-nav hstack gap-1">
                {/* Refresh Button - Reloads connector list */}
                <button className="btn btn-light" onClick={fetchData}>
                <CommonIcon iconName="RefreshCw" size={"14px"} color={"#000"} />
                </button>
                
                {/* Add Connector Button - Opens add/edit modal */}
                <button onClick={resetForm}
                  className="btn btn-primary uploadcontract-modal"
                  data-bs-toggle="modal"
                  data-bs-target="#addmemberModal"
                >
                  <CommonIcon iconName="Plus" size={"14px"} color="#fff" /> Add Connector
                </button>
              </div>
            </div>
          </div> :"" }

          {/* Filters Section (Hidden) */}
      <div className="row d-none" id="row_filter" >
      <div className="col-lg-1 mt-2">
      <CommonIcon iconName="Filter" size={"14px"}  /> Filters:
      </div>
      <div className="col-lg-3">
      {/* Filter dropdown placeholder
            hideSelectedOptions={false}
            components={{
              Option
            }}
            value={filters.filterdocumentType}
            onChange={(selectedOption) => handleFilterChange('filterdocumentType', selectedOption)}
            options={documentTypeOptions}
            placeholder="Clauses Type"
          /> */}
      </div>

      <div className="col-lg-3">
      {/* <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option
            }}
            value={filters.filterlicensor}
            onChange={(selectedOption) => handleFilterChange('filterlicensor', selectedOption)}
            options={licensorOptions}
            placeholder="Licensor"
          /> */}
      </div>
      <div className="col-lg-3 mb-2">
      {/* <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option
            }}
            value={filters.filterstatus}
            onChange={(selectedOption) => handleFilterChange('filterstatus', selectedOption)}
            options={statusoptions}
            placeholder="Status"
          /> */}
          </div>
      </div>
      </div>
          <div className="row">
            <div className="col-lg-12">
                  <div>
                    {/* DataTable Rendering Section */}
                    {loading ? (
                      // Show loading spinner while data loads
                 <PageLoader />
            ) :  data.length > 0 ?
                // Show DataTable if connectors exist
                (
                <div className="row  mt-4">
                  <div className="col-lg-12">
                    {/* jQuery DataTable Container */}
                    {/* Table is initialized in useEffect and populated with connectors data */}
                    <table
                      id="contract_grid"
                      className="display"
                      style={{ width: "100%" }}
                    >
                      <thead>
                      </thead>
                    </table>
                  </div>
                </div>) :     
                // Show "No Data Found" message if no connectors exist
                <div className="mt-5 card"><div className="text-center">
                  <div className="no-doc-container my-4">
                    {/* Empty state icon */}
                    <CommonIcon iconName="Database" width="50" height="50" />
                    <div className="mt-3">
                      <i className="bi bi-exclamation-circle-fill text-danger"></i>
                    </div>
                  </div>
                  <h4 className="text-muted mb-4">No Connector Found</h4>

                  {/* Add first connector button */}
                  <button onClick={resetForm}
                      className="btn btn-primary uploadcontract-modal mb-4"
                      data-bs-toggle="modal"
                      data-bs-target="#addmemberModal"
                    >
                      <CommonIcon iconName="Plus" size={"14px"} color="#fff" /> Add Connector
                    </button>
                    <br/>
                    <br/>
                </div>
                </div>
            }
                  </div>
                </div>
              </div>
        </div>
      </div>

      {/* ========================================
           DELETE CONFIRMATION MODAL
          ======================================== */}
      <Modal show={deletedocumentmodal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title><div className="page-title" id="confirmationModalLabel">
                Delete Connector
              </div></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Delete confirmation message with connector name */}
          <label className="form-label">Are you sure want to delete <span id="document_name"></span> connector?</label>
        </Modal.Body>
        <Modal.Footer>
          {/* Close button - dismisses modal without deleting */}
          <button type="button" onClick={handleDeleteClose} className="btn btn-light" data-bs-dismiss="modal">
            <CommonIcon iconName="X" size={"14px"} /> Close
          </button>
          {/* Delete button - calls handleDelete function to remove connector */}
          <button onClick={handleDelete} id="btn_delete" data-id="1111" type="submit" className="btn btn-primary">
            <CommonIcon iconName="Trash" size={"14px"} color="#fff" /> Delete
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========================================
           PROMPT VIEW MODAL
          ======================================== */}
      <Modal show={promptmodal} onHide={handlePromptClose}>
        <Modal.Header closeButton>
          <Modal.Title><div className="page-title">
                Prompt
              </div></Modal.Title>
              <p className="modal-desc">
                View system prompt and user prompt
            </p>
        </Modal.Header>
        <Modal.Body>
          <br/>
          <br/>
          {/* Display prompt data as JSON in pre-formatted text */}
          <pre className="bg-light p-3 rounded" style={{height:"200px"}}>
            {JSON.stringify(prompt_data, null, 4).replace(/\n/g, '')}
          </pre>
        </Modal.Body>
        <Modal.Footer>
        </Modal.Footer>
      </Modal>

      {/* ========================================
           ADD/EDIT CONNECTOR MODAL
          ======================================== */}
      <Modal show={addclausemodal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title> 
            {/* Modal title changes based on create vs edit mode */}
            {formData.taxonomyId==="" ? "Create New Connector" : "Edit Connector"}
           </Modal.Title>
           <p className="modal-desc">
             {formData.taxonomyId==="" ? "Add a new connector" : "Edit a connector"}
            </p>
        </Modal.Header>
        <Modal.Body>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                {/* Connector Name Field */}
                <div className="col-lg-12">
                  <label className="form-label">Connector Name</label>
                  <input type="hidden" name="taxonomyId" value={formData.taxonomyId} />
                  <input
                    type="text"
                    name="connectorName"
                    className="form-control"
                    value={formData.connectorName}
                    onChange={handleInputChange}
                    placeholder="Enter connector name"
                  />
                  {/* Validation error message */}
                  {errors.connectorName && <div className="text-danger">{errors.connectorName}</div>}
                </div>

                {/* Connector Type Dropdown */}
                <div className="col-lg-12">
                  <label className="form-label">Connector Type</label>
                  <Select
                    name="connectorType"
                    options={taxonomies_options}
                    value={formData.connectorType}
                    onChange={handleSelectChange}
                    placeholder="Select a Connector Type"
                  />
                  {/* Validation error message */}
                  {errors.connectorType && <div className="text-danger">{errors.connectorType}</div>}
                </div>

                {/* Organization ID Field */}
                <div className="col-lg-12">
                  <label className="form-label">Organization ID</label>
                  <input type="hidden" name="taxonomyId" value={formData.taxonomyId} />
                  <input
                    type="text"
                    name="connectorOrganizationID"
                    className="form-control"
                    value={formData.connectorOrganizationID}
                    onChange={handleInputChange}
                    placeholder="Enter organization ID"
                  />
                  {/* Validation error message */}
                  {errors.connectorOrganizationID && <div className="text-danger">{errors.connectorOrganizationID}</div>}
                </div>

                {/* API Token Field */}
                <div className="col-lg-12">
                  <label className="form-label">API Token</label>
                  <input type="hidden" name="taxonomyId" value={formData.taxonomyId} />
                  <input
                    type="text"
                    name="connectorAPIToken"
                    className="form-control"
                    value={formData.connectorAPIToken}
                    onChange={handleInputChange}
                    placeholder="Enter API token"
                  />
                  {/* Validation error message */}
                  {errors.connectorAPIToken && <div className="text-danger">{errors.connectorAPIToken}</div>}
                </div>

                {/* Secret Key Field */}
                <div className="col-lg-12">
                  <label className="form-label">Secret Key</label>
                  <input type="hidden" name="taxonomyId" value={formData.taxonomyId} />
                  <input
                    type="text"
                    name="connectorSecretKey"
                    className="form-control"
                    value={formData.connectorSecretKey}
                    onChange={handleInputChange}
                    placeholder="Enter secret key"
                  />
                  {/* Validation error message */}
                  {errors.connectorSecretKey && <div className="text-danger">{errors.connectorSecretKey}</div>}
                </div>
                
                {/* Loading indicator during form submission */}
                <div className="page_pro_load">
                  {<ProcessLoader pmMessage={"Saving connector, please wait..."}  />}
                </div>
                <div id="uploaderror"></div>
              </div>
            </form>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {/* Close button - dismisses modal without saving */}
          <button type="button" onClick={handleClose} className="btn btn-light" data-bs-dismiss="modal">
            <i className="ri-close-line align-bottom me-1"></i> Close
          </button>
          {/* Submit button - saves connector (create or update) */}
          <button onClick={handleSubmit} type="submit" className="btn btn-primary">
                    {formData.taxonomyId==="" ? "Create Connector" : "Update Connector"}
               </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};